import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useIsMounted } from '../useIsMounted';
/**
 * Basically a boolean trigger and some fancy props. We use this to help manage our dynamically loaded components
 * that need to load based on user input.
 *
 * E.g. the loadable version DaEditor that loads when the editor
 * is clicked on.
 *
 * Usage:
 * ```jsx
 * const {isLoadingTriggered, triggerProps} = useLoadableComponent()
 *
 * return (
 *    <>
 *      <button {...triggerProps}>Load that component!</button>
 *      {isLoadingTriggered && <LoadableDaEditorWrapper />}
 *    </>
 * )
 * ```
 */ export function useLoadableComponent({ forceLoadOnMount  } = {}) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoadingTriggered, setIsLoadingTriggered] = useState(false);
    const isMounted = useIsMounted();
    const triggerLoad = useCallback(()=>{
        !isLoadingTriggered && setIsLoadingTriggered(true);
    }, [
        isLoadingTriggered
    ]);
    const triggerProps = useMemo(()=>({
            onDragEnter: triggerLoad,
            onClick: triggerLoad
        }), [
        triggerLoad
    ]);
    const finishLoading = useCallback(()=>{
        setIsLoaded(true);
    }, []);
    useEffect(()=>{
        // We do the window check here because Suspense is client-side only.
        // SSR will throw an exception if we don't.
        if (isMounted && forceLoadOnMount && typeof window !== 'undefined') {
            triggerLoad();
        }
    }, [
        forceLoadOnMount,
        isMounted,
        triggerLoad
    ]);
    return {
        triggerProps,
        isLoadingTriggered,
        triggerLoad,
        isLoaded,
        finishLoading
    };
}
/**
 *
 * A context version of our useLoadableComponent hook. Refer there for more info.
 *
 * Please use `<LoadableComponentContextProvider />` instead of `<LoadableComponentContext.Provider />`.
 *
 * Usage:
 * ```jsx
 * const { isLoadingTriggered, triggerProps } = useContext(LoadableComponentContext)
 * ```
 */ export const LoadableComponentContext = /*#__PURE__*/ createContext({
    isLoadingTriggered: false,
    isLoaded: false,
    triggerProps: {
        onClick: ()=>null,
        onDragEnter: ()=>null
    },
    finishLoading: ()=>null,
    triggerLoad: ()=>null
});
var LoadableComponentContextProvider = (_param)=>{
    var { children  } = _param, options = _object_without_properties(_param, [
        "children"
    ]);
    const loadableComponentState = useLoadableComponent(options);
    const memoizedLoadableComponentState = useMemo(()=>loadableComponentState, [
        loadableComponentState
    ]);
    return /*#__PURE__*/ React.createElement(LoadableComponentContext.Provider, {
        value: memoizedLoadableComponentState
    }, children);
};
/**
 * Bundles our `useLoadableComponent` hook with our `LoadableComponentContext`.
 */ export { LoadableComponentContextProvider };
